import { Injectable, Inject } from '@angular/core';
import { Instance } from '../interfaces/instance.interface';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { InstanceSelector } from '../interfaces/instanceSelector.interface';
import { Language } from '../interfaces/language.interface';

@Injectable()
export class SessionService {
  constructor(
    protected http: HttpClient,
    protected router: Router,
    protected spinner: NgxSpinnerService,
    @Inject('API_URL') private apiUrl: string,
    @Inject('LOCAL_STORAGE_SESSION_ITEM_NAME') private itemName: string,
    @Inject('LANGUAGES') private languages: Language[]) {
  }

  private session: Session;

  public instances: Instance[];

  public activeInstanceId: string;
  public activeInstancePersonalNumber: string;
  public activeInstanceRole: string;

  public get numberOfInstances(): number {
    return this.instances ? this.instances.length : 0;
  };

  public get activeInstance(): Instance {
    const emptyInstance: Instance = {
      id: '',
      name: '',
      roleType: '',
      roleName: '',
      personalNumber: '',
      defaultUrl: ''
    };

    if (!this.instances?.length) {
      return emptyInstance;
    }

    return this.instances.find(i =>
      i.personalNumber === this.activeInstancePersonalNumber &&
      i.id === this.activeInstanceId &&
      i.personalNumber === this.activeInstancePersonalNumber &&
      i.roleType === this.activeInstanceRole) ||
      emptyInstance;
  }

  public get hasActiveInstance(): boolean {
    return !!this.activeInstanceId && !!this.activeInstancePersonalNumber && !!this.activeInstanceRole && !!this.activeInstance.id;
  }

  public get hasSession(): boolean {
    return !!this.session;
  }

  public get hasToken(): boolean {
    return this.hasSession && !!this.session.accessToken;
  }

  public setSession(session: Session) {
    this.session = session;
    this.storeSession();
  }

  public storeSession() {
    if (!this.session) {
      localStorage.removeItem(this.itemName);
    } else {
      localStorage.setItem(this.itemName, JSON.stringify(this.session));
    }
  }

  public getSession(): Session {
    if (this.session) {
      return this.session;
    }

    const newSession = (): Session => {
      this.session = new Session();
      this.storeSession()
      return this.session;
    }

    const json: string = localStorage.getItem(this.itemName);
    if (!json || json === 'null' || json === 'undefined') {
      return newSession();
    }

    try {
      this.session = JSON.parse(json) as Session;
      if (this.languages.map(lang => lang.id).indexOf(this.session.language) === -1)
        this.session.language = this.languages[0].id;

    } catch (error) {
      console.error(error);
      return newSession();
    }

    return this.session;
  }

  public removeSession() {
    localStorage.removeItem(this.itemName);
    this.session = null;
  }

  public setInstance(instance: Instance, route?: string) {
    this.session = this.getSession();

    if (instance?.id && instance.roleType) {
      this.spinner.show();
      this.http.post(`${this.apiUrl}SetLoginType`, {
        loginType: instance.roleType,
        instanceId: instance.id
      }).toPromise()
        .then(() => {
          // Only log login for arbetstagare
          this.http.post(`${this.apiUrl}LogLogin`, {
            instanceId: instance.id
          }).toPromise()
            .then(() => {
              this.spinner.hide();
              if (instance) {
                this.setActiveInstance(instance.id, instance.personalNumber, instance.roleType, route || instance.defaultUrl);
              } else {
                this.setActiveInstance(null, null, null, null);
              }
            });
        })
        .catch(error => {
          this.spinner.hide();
          console.error(error);
        });
    } else {
      this.setActiveInstance(instance ? instance.id : null, instance ? instance.personalNumber : null, instance ? instance.roleType : null, route);
    }
  }

  private setActiveInstance(id: string, personalNumber: string, role: string, route?: string) {
    this.getInstances().subscribe(
      (instanceSelector: InstanceSelector) => {
        this.activeInstanceId = id;
        this.activeInstancePersonalNumber = personalNumber;
        this.activeInstanceRole = role;

        if (!this.hasActiveInstance) {
          this.activeInstanceId = null;
          this.activeInstancePersonalNumber = null;
          this.activeInstanceRole = null;
          if (!this.router.url.includes('mina-meddelanden'))
            this.router.navigate(['/select-instance'], { queryParams: { enforce: true } });
          return;
        }

        this.storeSession();

        if (route) {
          this.router.navigate([route]);
        }
      },
      (error) => {
        this.instances = [];
      });
  }

  public setToken(token: string) {
    this.session = this.getSession();
    this.session.accessToken = token;
    this.storeSession();
  }

  public getToken(): string {
    this.session = this.getSession();
    if (!this.session) {
      return null;
    }

    return this.session.accessToken;
  }

  public logout() {
    this.activeInstanceId = null;
    this.activeInstancePersonalNumber = null;
    this.activeInstanceRole = null;
    this.http.get(`${this.apiUrl}Logout`)
      .toPromise()
      .then(_ => {
        this.session.accessToken = null;
        this.storeSession();
      })
      .catch(error => {
        this.session.accessToken = null;
        this.storeSession();
        console.error(error);
      });
  }

  public getLanguage(): string {
    this.session = this.getSession();
    if (!this.session)
      return null;

    return this.session.language;
  }

  public setLanguage(language: string) {
    this.session = this.getSession();
    this.session.language = language;
    this.storeSession();
  }

  public getPersonalNumber(): string {
    this.session = this.getSession();
    if (!this.session)
      return null;

    return this.session.personalNumber;
  }

  public setPersonalNumber(personalNumber: string) {
    this.session = this.getSession();
    this.session.personalNumber = personalNumber;
    this.storeSession();
  }

  public getLoginMethod(): string {
    this.session = this.getSession();
    return this.session.loginMethod;
  }

  public setLoginMethod(loginMethod: string) {
    this.session = this.getSession();
    this.session.loginMethod = loginMethod;
    this.storeSession();
  }

  public selectInstance(callback?: () => void) {
    this.getInstances().subscribe(
      (instanceSelector: InstanceSelector) => {
        if (this.hasActiveInstance) {
          (callback || (() =>
            this.setActiveInstance(this.activeInstanceId, this.activeInstancePersonalNumber, this.activeInstanceRole)))();
          return;
        } else {
          this.activeInstanceId = null;
          this.activeInstancePersonalNumber = null;
          this.activeInstanceRole = null;
        }
        if(this.router.url.includes('mina-meddelanden'))
          callback();
        else
          this.router.navigate(['/select-instance'], { queryParams: { enforce: this.hasActiveInstance } });
      });
  }

  public getInstances(): Observable<InstanceSelector> {
    return this.http.get<InstanceSelector>(`${this.apiUrl}instances/get`).pipe(map(
      (instanceSelector) => {
        this.instances = instanceSelector.instances;
        return instanceSelector;
      }));
  }
}

export class Session {
  public accessToken: string;
  public language: string = 'se';
  public personalNumber: string;
  public loginMethod: string;
}
